import type { RefObject } from "react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Center,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Image,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { PurchasingCartModal } from "./PurchasingCartModal";
import { RegistrationModal } from "./RegistrationModal";
import type { DealFinderTicker } from "api/tickers/models/DealFinderTicker";
import type { ProductOptionStatus, PurchaseStatus } from "api/billingPlan/models/PurchaseStatus";
import type { ComponentInfo } from "api/billingPlan/models/ComponentInfo";
import type { AlreadyPurchased } from "api/billingPlan/models/AlreadyPurchased";
import { getPurchaseStatus } from "api/billingPlan";
import verifiedShield from "screens/common/static/misc/verified_shield_logo.png";
import soc2logo from "screens/common/static/logos/soc_logo.png";
import { useConversationContext } from "screens/thread/ConversationContext";
import { useConversationDialogSubmitFunctions } from "screens/thread/useConversationDialogSubmitFunctions";
import { DEAL_FINDER_ANALYZE_COMPANY, DEAL_FINDER_DOWNLOAD_REPORT, DEAL_FINDER_PURCHASE_REPORT } from "api/analytics/events";
import { track } from "api/analytics";
import { useUserProfile } from "hooks";
import { useCopyValue } from "hooks/useCopies";
import { MarkdownViewer } from "screens/markdown/MarkdownViewer";
import { UpgradePlanButton } from "screens/panels/settings/tabs/subscription/SubscriptionUpgrade";
import { useDispatch, useSelector } from "react-redux";
import { registerUser } from "state/session/operations";
import { useNavigate } from "react-router-dom";
import {
  isDealTierFree,
  useCheckDealRestricted,
  useCheckDealStatusAllowed,
  useCheckUserCurrentCredit,
  useCheckUserHasCredit,
} from "../utils/dealUtils";
import type { RootState } from "state/rootReducer";
import { actions as pendingCreditsActions } from "state/pendingCredits/reducer";
import { v4 as uuid } from "uuid";

interface BaseProductOption {
  id: string;
  name: string;
  description: string;
  price: string;
  originalPrice?: string;
  subtitle?: string;
  status: "purchased" | "available_for_purchase" | "not_available_for_purchase";
  requiresPaymentMethod?: boolean;
}

interface PurchasedProductOption extends BaseProductOption {
  status: "purchased";
  downloadUrl: string | null;
}

type ProductOption = BaseProductOption | PurchasedProductOption;

interface PurchaseDealRightSectionProps {
  deal: DealFinderTicker;
  rightColumnRef: RefObject<HTMLDivElement>;
  rightScrollbarStyle: any; // Using any to avoid type issues with css prop
  purchaseBgColor: string;
  purchaseSelectedBgColor: string;
  footerBgColor: string;
  borderColor: string;
  headerColor: string;
  purchaseHeading: string;
  purchaseDescription: string;
  id: string;
  ctaButtonProps: Record<string, unknown>;
  isMobile: boolean;
  onClose: () => void;
  isAnonymousUser: boolean;
}

const isPurchasedOption = (option: ProductOptionStatus): option is ComponentInfo & AlreadyPurchased => {
  return option.status === "purchased";
};
const isPurchasedProductOption = (option: ProductOption): option is PurchasedProductOption => {
  return option.status === "purchased" && "downloadUrl" in option;
};

export const PurchaseDealRightSection: React.FC<PurchaseDealRightSectionProps> = ({
  deal,
  rightColumnRef,
  rightScrollbarStyle,
  purchaseBgColor,
  purchaseSelectedBgColor,
  footerBgColor,
  borderColor,
  headerColor,
  purchaseHeading,
  purchaseDescription,
  id,
  ctaButtonProps,
  isMobile,
  onClose,
  isAnonymousUser,
}) => {
  // State management moved from parent
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [purchaseStatus, setPurchaseStatus] = useState<PurchaseStatus | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { fullName, email } = useUserProfile();
  const analyzeCompanyDescription = useCopyValue("copy_deal_analyze_company_description");
  const freeLabel = useCopyValue("copy_deal_finder_free_label");
  const purchaseDescriptionAnonymous = useCopyValue("copy_markdown_investment_purchase_description_anonymous");
  const registerButtonLabel = useCopyValue("copy_investment_purchase_button_label_register");
  const upgradeButtonLabel = useCopyValue("copy_investment_purchase_button_label_upgrade");
  const startColor = useColorModeValue("gray.200", "gray.900");
  const endColor = useColorModeValue("gray.50", "gray.800");
  const upgradeFontColor = useColorModeValue("gray.800", "gray.200");
  const upgradeBgColor = useColorModeValue("yellow.300", "yellow.800");
  const registerFontColor = useColorModeValue("gray.100", "gray.200");
  const registerBgColor = useColorModeValue("primary.default", "green.800");
  const originalPriceColor = useColorModeValue("red.500", "red.400");
  const { isLoading: isSubscriptionDetailsLoading } = useSelector((state: RootState) => state.subscriptions);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Local state
  const [isPurchasingCartOpen, setIsPurchasingCartOpen] = useState<boolean>(false);
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState<boolean>(false);
  const hasAutoSelectedRef = useRef(false);

  // Conversation context for due diligence
  const { setSelectedQuestionAndFocus } = useConversationContext();
  const { onSubmitDueDiligenceProject } = useConversationDialogSubmitFunctions({});

  const isRestricted = useCheckDealRestricted(deal.symbol);
  const hasEnoughCredit = useCheckUserHasCredit(deal.creditCosts?.analysis || null);
  const usersCurrentCreditStatus = useCheckUserCurrentCredit();
  const isDealStatusAllowed = useCheckDealStatusAllowed(deal.status);
  const isTierFree = isDealTierFree(deal);

  // Fetch purchase status when component mounts
  useEffect(() => {
    if (deal && id && !isAnonymousUser && !isSubscriptionDetailsLoading) {
      setIsLoading(true);
      setError(null);

      getPurchaseStatus(id, deal.id)
        .then((status) => {
          setPurchaseStatus(status);
        })
        .catch((err) => {
          setPurchaseStatus(null);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [deal, id, isAnonymousUser, isSubscriptionDetailsLoading]);

  // Submit due diligence function moved from parent
  const submitDueDiligence = useCallback(
    (event: { stopPropagation: () => void }) => {
      event.stopPropagation();
      if (!deal || !deal.symbol || !deal.exchange) {
        return;
      }

      // Add pending credit deduction if there's a credit cost
      if (deal.creditCosts?.analysis && deal.creditCosts.analysis > 0) {
        dispatch(
          pendingCreditsActions.addPendingDeduction({
            id: uuid(),
            amount: deal.creditCosts.analysis,
          })
        );
      }

      setSelectedQuestionAndFocus({
        projectTicker: deal.symbol,
        projectExchange: deal.exchange,
      });

      onSubmitDueDiligenceProject({
        ticker: deal.symbol,
        exchange: deal.exchange,
        companyName: deal.name,
        companyDescription: deal.companyShortDescription || undefined,
        companyWebsite: deal.companyUrl || undefined,
        companyLogoUrl: deal.companyLogoUrl || undefined,
      });
      onClose();
    },
    [deal, setSelectedQuestionAndFocus, onSubmitDueDiligenceProject, onClose, dispatch]
  );

  // Handle registration submission
  const handleRegistrationSubmit = useCallback(
    (fields: { firstName: string; lastName: string; username: string; investmentInterest: string; password: string }) => {
      dispatch(registerUser(fields));
    },
    [dispatch]
  );

  const productOptions = React.useMemo(() => {
    if (isAnonymousUser) {
      return [];
    }

    const purchaseOptions: ProductOption[] = purchaseStatus
      ? Object.entries(purchaseStatus).map(([key, option]): ProductOption => {
          const baseOption: BaseProductOption = {
            id: key,
            name: option.componentName || key,
            description: option.componentDescription || "Detailed analysis and insights",
            price: option.status === "purchased" ? "Purchased" : option.price,
            originalPrice: option.status === "available_for_purchase" && option.originalPrice ? option.originalPrice : undefined,
            status: option.status,
            requiresPaymentMethod: option.status === "available_for_purchase" ? option.requiresPaymentMethod : undefined,
          };

          if (isPurchasedOption(option)) {
            return {
              ...baseOption,
              status: "purchased" as const,
              downloadUrl: option.downloadUrl,
            };
          }

          return baseOption;
        })
      : [];

    const additionalOption: BaseProductOption = {
      id: "due_diligence",
      name: "Analyze Company",
      description: analyzeCompanyDescription,
      price:
        deal.creditCosts?.analysis === 0
          ? freeLabel
          : !isDealStatusAllowed
          ? "Coming Soon"
          : !hasEnoughCredit && deal.creditCosts?.analysis !== 0
          ? "Upgrade"
          : `${deal.creditCosts?.analysis} Credits`,
      status: "available_for_purchase",
      subtitle:
        deal.creditCosts?.analysis === 0 || !isDealStatusAllowed
          ? ""
          : hasEnoughCredit
          ? `You have ${usersCurrentCreditStatus.remaining} credits available`
          : `Requires ${deal.creditCosts?.analysis} credits, you have ${usersCurrentCreditStatus.remaining} remaining`,
      requiresPaymentMethod: undefined,
    };

    const allOptions = [...purchaseOptions, additionalOption];
    return allOptions;
  }, [
    isAnonymousUser,
    purchaseStatus,
    analyzeCompanyDescription,
    deal.creditCosts?.analysis,
    freeLabel,
    isDealStatusAllowed,
    hasEnoughCredit,
    usersCurrentCreditStatus.remaining,
  ]);

  useEffect(() => {
    if (purchaseStatus !== null) {
      hasAutoSelectedRef.current = false;
    }
  }, [purchaseStatus]);

  useEffect(() => {
    if (productOptions.length > 0 && !isLoading && !hasAutoSelectedRef.current) {
      const firstAvailableOption = productOptions.find((option) => option.status !== "purchased");
      if (firstAvailableOption) {
        isDealStatusAllowed && setSelectedOption(firstAvailableOption.id);
        hasAutoSelectedRef.current = true;
      }
    }
  }, [productOptions, isLoading, isDealStatusAllowed]);

  const getButtonText = () => {
    if (!isDealStatusAllowed) {
      return "Coming Soon";
    }
    if (isTierFree) {
      return productOptions.find((option) => option.id === selectedOption)?.id === "due_diligence"
        ? "Analyze for free"
        : "Download for Free";
    }
    if (productOptions.find((option) => option.id === selectedOption)?.id === "due_diligence") {
      return isRestricted ? "Restricted Access" : "Analyze Company";
    }

    const selectedStatus = purchaseStatus?.[selectedOption];

    if (selectedStatus && isPurchasedOption(selectedStatus)) {
      return selectedStatus.downloadUrl ? "Download Report" : "Processing...";
    }

    return "Purchase Report";
  };

  const skeletonStyle = {
    startColor: startColor,
    endColor: endColor,
  };

  return (
    <Stack
      borderLeft="1px"
      borderColor={borderColor}
      bg={purchaseBgColor}
      p="1.5rem"
      pb="0"
      overflowY="auto"
      css={rightScrollbarStyle}
      ref={rightColumnRef}
      width="100%"
      height="100%"
      direction="column"
      justifyContent={"space-between"}>
      <Box borderRadius="md" flex="1" display="flex" flexDirection="column">
        <Heading px="1rem" size="md" color={headerColor} mb={4} textAlign={"center"}>
          {purchaseHeading}
        </Heading>
        <Box mb={6}>
          <MarkdownViewer content={purchaseDescription} fontSize="sm" backgroundColor="transparent" />
        </Box>
        {isLoading && productOptions.length === 1 ? (
          <Stack position={"relative"} spacing={"2px"} width="100%" justifyContent={"space-between"} alignItems="flex-start" mb="1.5rem">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} {...skeletonStyle} borderRadius={"none"} height="64.6px" width="100%" />
            ))}
          </Stack>
        ) : error ? (
          <Center height="14rem">
            <Box px="1rem" py="1rem" borderWidth="1px" borderColor={borderColor} bg={footerBgColor} borderRadius="md" width="100%">
              <Text fontSize={"md"} textAlign={"center"}>
                {error}
              </Text>
            </Box>
          </Center>
        ) : (
          <RadioGroup onChange={setSelectedOption} value={selectedOption} mb={6}>
            <Stack spacing={0}>
              {productOptions.map((option) => {
                const isPurchased = isPurchasedProductOption(option);
                const isProcessing = isPurchased && !option.downloadUrl;

                return (
                  <Box
                    key={option.id}
                    position="relative"
                    p={4}
                    fontSize="sm"
                    border="1px"
                    bgColor={selectedOption === option.id ? purchaseSelectedBgColor : footerBgColor}
                    borderColor={selectedOption === option.id ? headerColor : borderColor}
                    borderRadius="none"
                    onClick={() => !isProcessing && !isLoading && isDealStatusAllowed && setSelectedOption(option.id)}
                    cursor={isProcessing || isLoading || !isDealStatusAllowed ? "default" : "pointer"}
                    opacity={isProcessing || isLoading || !isDealStatusAllowed ? 0.6 : 1}
                    _hover={isProcessing || isLoading || !isDealStatusAllowed ? {} : { borderColor: "blue.300" }}
                    transition="all 0.2s"
                    boxShadow={selectedOption === option.id ? "0 0 0 1px blue.500" : "none"}>
                    <Flex justifyContent="space-between" alignItems="center">
                      <Stack spacing={0} position="relative">
                        <HStack>
                          <Radio
                            isDisabled={isProcessing || isLoading || !isDealStatusAllowed}
                            value={option.id}
                            colorScheme="blue"
                            isChecked={selectedOption === option.id}
                            onChange={() => setSelectedOption(option.id)}
                          />
                          <Stack direction="row" spacing="0">
                            <Center>
                              <Text fontSize="md" fontWeight="medium">
                                {option.name}
                              </Text>
                            </Center>
                            <Popover trigger="hover">
                              <PopoverTrigger>
                                <IconButton
                                  opacity={isProcessing || isLoading ? 0 : 1}
                                  pb="15px"
                                  pr="10px"
                                  aria-label="Info"
                                  icon={<Icon as={IoMdInformationCircleOutline} boxSize="1rem" cursor="pointer" />}
                                  variant="ghost"
                                  colorScheme="blue"
                                  _hover={{}}
                                  _active={{}}
                                  size="sm"
                                />
                              </PopoverTrigger>
                              <PopoverContent>
                                <PopoverBody>
                                  <Text fontSize="sm">{option.description}</Text>
                                </PopoverBody>
                              </PopoverContent>
                            </Popover>
                          </Stack>
                        </HStack>
                      </Stack>
                      <HStack>
                        {option.originalPrice && (
                          <Text
                            color={originalPriceColor}
                            fontWeight="medium"
                            fontSize="md"
                            position="relative"
                            sx={{
                              "&::after": {
                                content: '""',
                                position: "absolute",
                                width: "100%",
                                height: "2px",
                                backgroundColor: originalPriceColor,
                                top: "50%",
                                left: 0,
                                transform: "translateY(-50%)",
                              },
                            }}>
                            {`${option.originalPrice}`}
                          </Text>
                        )}
                        <Text
                          fontWeight="medium"
                          fontSize={option.price === "Purchased" || !hasEnoughCredit || !isDealStatusAllowed ? "md" : "lg"}>
                          {isProcessing ? "Processing..." : option.price}
                        </Text>
                      </HStack>
                      {option.subtitle && (
                        <Text fontSize="xs" color="gray.500" textAlign={"right"} position="absolute" bottom="5px" right="0" pr="1rem">
                          {option.subtitle}
                        </Text>
                      )}
                    </Flex>
                  </Box>
                );
              })}
            </Stack>
          </RadioGroup>
        )}
        {isAnonymousUser ||
        (!hasEnoughCredit &&
          productOptions.find((option) => option.id === selectedOption)?.id === "due_diligence" &&
          !isLoading &&
          hasAutoSelectedRef.current) ? (
          <Box width="100%">
            {isAnonymousUser && (
              <Box mb={6}>
                <MarkdownViewer content={purchaseDescriptionAnonymous} fontSize="sm" backgroundColor="transparent" />
              </Box>
            )}
            <UpgradePlanButton
              tooltip=""
              label={isAnonymousUser ? registerButtonLabel : upgradeButtonLabel}
              buttonSize="md"
              onClick={() => {
                isAnonymousUser ? setIsRegistrationModalOpen(true) : navigate("/pricing");
              }}
              style={{
                width: "100%",
                marginBottom: 4,
                color: isAnonymousUser ? registerFontColor : upgradeFontColor,
                _hover: {
                  backgroundColor: isAnonymousUser ? registerBgColor : upgradeBgColor,
                  borderColor: isAnonymousUser ? registerBgColor : upgradeBgColor,
                },
                _active: {
                  backgroundColor: isAnonymousUser ? registerBgColor : upgradeBgColor,
                  borderColor: isAnonymousUser ? registerBgColor : upgradeBgColor,
                },
                backgroundColor: isAnonymousUser ? registerBgColor : upgradeBgColor,
                borderColor: isAnonymousUser ? registerBgColor : upgradeBgColor,
              }}
            />
          </Box>
        ) : (
          <Button
            {...ctaButtonProps}
            className="ch-deal-finder-purchase-button"
            width="100%"
            borderRadius="md"
            mb={4}
            isDisabled={
              getButtonText() === "Processing..." ||
              isAnonymousUser ||
              error === "No Analyzed Purchase Options" ||
              isLoading ||
              !selectedOption ||
              (!isTierFree && isRestricted && selectedOption === "due_diligence") ||
              (!hasEnoughCredit && selectedOption === "due_diligence") ||
              (!isDealStatusAllowed && selectedOption === "due_diligence")
            }
            onClick={(event) => {
              const selectedProduct = purchaseStatus && purchaseStatus[selectedOption];
              if (productOptions.find((option) => option.id === selectedOption)?.id === "due_diligence") {
                track(DEAL_FINDER_ANALYZE_COMPANY, {
                  dealName: deal?.name,
                  dealExchange: deal?.exchange,
                  dealTicker: deal?.symbol,
                  userName: fullName,
                  userEmail: email,
                });
                submitDueDiligence(event);
              } else if (selectedProduct && isPurchasedOption(selectedProduct) && selectedProduct.downloadUrl) {
                track(DEAL_FINDER_DOWNLOAD_REPORT, {
                  dealName: deal?.name,
                  dealExchange: deal?.exchange,
                  dealTicker: deal?.symbol,
                  reportType: selectedOption,
                  userName: fullName,
                  userEmail: email,
                });
                window.open(selectedProduct.downloadUrl, "_blank");
              } else {
                track(DEAL_FINDER_PURCHASE_REPORT, {
                  dealName: deal?.name,
                  dealExchange: deal?.exchange,
                  dealTicker: deal?.symbol,
                  reportType: selectedOption,
                  userName: fullName,
                  userEmail: email,
                });
                setIsPurchasingCartOpen(true);
              }
            }}>
            {getButtonText()}
          </Button>
        )}

        {isPurchasingCartOpen && purchaseStatus && selectedOption && (
          <PurchasingCartModal
            isOpen={isPurchasingCartOpen}
            onClose={(success) => {
              setIsPurchasingCartOpen(false);
              if (success && deal && id && !isAnonymousUser) {
                setIsLoading(true);
                setError(null);
                getPurchaseStatus(id, deal.id)
                  .then((status) => {
                    setPurchaseStatus(status);
                  })
                  .catch((err) => {
                    console.error("Error fetching purchase status:", err);
                  })
                  .finally(() => {
                    setIsLoading(false);
                  });
              }
            }}
            deal={deal}
            selectedProductOption={purchaseStatus[selectedOption]}
            selectedProductKey={selectedOption}
          />
        )}

        {isRegistrationModalOpen && (
          <RegistrationModal
            isOpen={isRegistrationModalOpen}
            onClose={() => setIsRegistrationModalOpen(false)}
            onSubmit={handleRegistrationSubmit}
          />
        )}
      </Box>
      <Stack pb="1rem">
        {!isMobile && (
          <HStack justifyContent={"flex-end"} spacing="1rem" width="100%">
            <Image src={verifiedShield} alt="Verified Shield" height="4rem" />
            <Image src={soc2logo} alt="SOC 2 Logo" height="4rem" />
          </HStack>
        )}
      </Stack>
    </Stack>
  );
};
